import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import {
	FieldValues,
	UseFormGetValues,
	UseFormSetValue
} from 'react-hook-form';
import { io, Socket } from 'socket.io-client';
import axios from 'axios';
import {
	AIResultType,
	ChatMessage,
	ChatMessageType,
	ConversationState,
	SelectorWSMessage,
	ServerErrorMessage,
	StateChangeCallbackContent,
	ToolCallSuccessWSMessage,
	ToolCallWSMessage,
	WsExceptionMessage
} from './WSCommunicationTypes';
import { SELECTORS } from './constants';
import { v4 as uuidv4 } from 'uuid';

export const CRAWLING_METHOD_VALUES = ['curl', 'impit', 'axios', 'crawlee'];

export const DEFAULT_MODEL = 'gpt-5-nano';

type SelectorHistoryItem = {
	id: string;
	name: string;
};

type SelectorHistory = Record<string, SelectorHistoryItem[]>;

export type SchemaSelectorFinderSession = {
	aiModel: string;
	canSearch: boolean;
	chatMessages: ChatMessage[];
	conversationState: ConversationState | undefined;
	crawlingMethod: string;
	customAIModel: string;
	handlePauseConversation: () => void;
	handleSearch: () => void;
	handleUserMessage: (msg: string) => boolean;
	isGettingSomeSelectorValue: boolean;
	isSearching: boolean;
	productUrl: string | undefined;
	selectorHistory: SelectorHistory;
	setAIModel: Dispatch<SetStateAction<string>>;
	setCrawlingMethod: Dispatch<SetStateAction<string>>;
	setCustomAIModel: Dispatch<SetStateAction<string>>;
	setProductUrl: (value: string) => void;
	setSelector: (
		selectorName: string,
		selectorValue: string | number | null
	) => void;
	setUseAI: Dispatch<SetStateAction<boolean>>;
	testAllSelectors: () => Promise<void>;
	handleDownloadHtml: () => Promise<void>;
	useAI: boolean;
	isDownloadingHtml: boolean;
	getSelectedValue: (selectorName: string, selector: string) => Promise<void>;
	availableModels: string[];
};

function createHtmlDownloadFilename(
	productUrl: string,
	method?: string
): string {
	try {
		const { hostname, pathname } = new URL(productUrl);
		const pathPart =
			pathname === '/' ? 'index' : pathname.replace(/\//g, '_');
		const sanitizedName = `${hostname}${pathPart}`.replace(
			/[^a-z0-9._-]+/gi,
			'_'
		);
		const methodName = method || 'curl';
		return `${sanitizedName || 'download'}-${methodName}.html`;
	} catch {
		return 'download.html';
	}
}

export type useSchemaSelectorFinderSessionProps = {
	getValues: UseFormGetValues<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	watchedProductUrl: any;
};

export function useSchemaSelectorFinderSession({
	getValues,
	setValue,
	watchedProductUrl
}: useSchemaSelectorFinderSessionProps): SchemaSelectorFinderSession {
	const selectorToSelectorName = useRef(new Map<string, string>());
	const socketRef = useRef<Socket | undefined>();
	const [socket, setSocket] = useState<Socket | undefined>();
	const [productUrl, setProductUrlState] = useState<string | undefined>();
	const productUrlRef = useRef<string | undefined>();
	const [wsConnected, setWsConnected] = useState(true);
	const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
	const [conversationState, setConversationState] = useState<
		ConversationState | undefined
	>(undefined);
	const conversationId = useRef<string | undefined>(undefined);
	const [selectorHistory, setSelectorHistory] = useState<SelectorHistory>({});
	const [isGettingSelectorValue, setIsGettingSelectorValue] = useState<
		Record<string, boolean>
	>({});
	const [crawlingMethod, setCrawlingMethod] = useState('');
	const [useAI, setUseAI] = useState(true);
	const [aiModel, setAIModel] = useState(DEFAULT_MODEL);
	const [customAIModel, setCustomAIModel] = useState('');
	const [isDownloadingHtml, setIsDownloadingHtml] = useState(false);
	const [availableModels, setAvailableModels] = useState<string[]>([
		DEFAULT_MODEL
	]);

	const effectiveConversationState = useMemo(() => {
		if (wsConnected) {
			return conversationState;
		}
		return ConversationState.Disconnected;
	}, [wsConnected, conversationState]);

	useEffect(() => {
		fetch('https://api.vero.prosoom.com/v1/models')
			.then((response) => response.json())
			.then((result) => {
				const openAIModels = result.available_models
					.filter((m: any) => m.provider === 'openai')
					.map((m: any) => m.name);
				setAvailableModels(openAIModels);
			});
	}, []);

	useEffect(() => {
		if (watchedProductUrl === productUrlRef.current) {
			return;
		}
		productUrlRef.current = watchedProductUrl;
		setProductUrlState(watchedProductUrl);
	}, [watchedProductUrl]);

	const setProductUrl = useCallback((value: string) => {
		productUrlRef.current = value;
		setProductUrlState(value);
	}, []);

	const onReceiveActualValue = useCallback(
		(expectedName: string, expectedValue: string | number | null) => {
			setValue(expectedName, `${expectedValue}`);
		},
		[setValue]
	);

	const getSelectedValue = useCallback(
		async (selectorName: string, selector: string) => {
			if (
				productUrlRef.current === undefined ||
				productUrlRef.current === ''
			) {
				return;
			}
			if (selector) {
				selectorToSelectorName.current.set(selector, selectorName);
			}
			try {
				setIsGettingSelectorValue((prevState) => ({
					...prevState,
					[selectorName]: true
				}));
				const res = await axios.post(
					`${process.env.REACT_APP_SELECTOR_TESTER_SERVICE_URL}/shops/call-tool`,
					{
						toolName: 'use_selector',
						toolArguments: JSON.stringify({
							selectorName,
							selector
						}),
						productUrl: productUrlRef.current,
						method: crawlingMethod
					}
				);
				if (res.status >= 200 && res.status < 300) {
					onReceiveActualValue(
						selectorName.replace('selector', 'actual'),
						res.data.toolOutput
					);
				}
			} catch (error: any) {
				console.error(error);
			} finally {
				setIsGettingSelectorValue((prevState) => ({
					...prevState,
					[selectorName]: false
				}));
			}
		},
		[crawlingMethod, onReceiveActualValue]
	);

	const isGettingSomeSelectorValue = useMemo(() => {
		for (const value of Object.values(isGettingSelectorValue)) {
			if (value) {
				return true;
			}
		}
		return false;
	}, [isGettingSelectorValue]);

	const setSelector = useCallback(
		(selectorName: string, selectorValue: string | number | null) => {
			const selectorValueString =
				typeof selectorValue === 'object'
					? JSON.stringify(selectorValue)
					: `${selectorValue}`;
			selectorToSelectorName.current.set(
				selectorValueString,
				selectorName
			);
			setSelectorHistory((prevState) => {
				const historyForSelector = prevState[selectorName] ?? [];
				if (
					historyForSelector.some(
						(value) => value.name === selectorValueString
					)
				) {
					return prevState;
				}

				return {
					...prevState,
					[selectorName]: [
						...historyForSelector,
						{ id: selectorValueString, name: selectorValueString }
					]
				};
			});
			setTimeout(() => setValue(selectorName, selectorValueString), 100);
			if (
				productUrlRef.current !== '' &&
				productUrlRef.current !== undefined
			) {
				void getSelectedValue(selectorName, selectorValueString);
			}
		},
		[getSelectedValue, setValue]
	);

	const onReceiveSelector = useCallback(
		(selectorName: string, selectorValue: string | number | null) => {
			setChatMessages((prevItems) => [
				...prevItems,
				{
					t: ChatMessageType.NewSelector,
					msg: {
						key: selectorName,
						value: selectorValue
					}
				}
			]);
			setSelector(selectorName, selectorValue);
		},
		[setSelector]
	);

	useEffect(() => {
		if (!process.env.REACT_APP_SELECTOR_TESTER_SERVICE_URL) {
			console.log(
				`process.env.REACT_APP_SELECTOR_TESTER_SERVICE_URL = ${process.env.REACT_APP_SELECTOR_TESTER_SERVICE_URL}`
			);

			setChatMessages((prevItems) => [
				...prevItems,
				{
					t: ChatMessageType.UserError,
					msg: 'Selector-Test-Service URL nicht konfiguriert'
				}
			]);
			return;
		}

		const newSocket = io(
			process.env.REACT_APP_SELECTOR_TESTER_SERVICE_URL,
			{
				transports: ['websocket'],
				withCredentials: true
			}
		);

		socketRef.current = newSocket;
		setSocket(newSocket);

		const allValues = getValues();
		const newSelectorHistory: SelectorHistory = {};
		for (const selectorName of SELECTORS) {
			if (
				!Object.prototype.hasOwnProperty.call(allValues, selectorName)
			) {
				continue;
			}
			const currentValue: string | undefined = allValues[selectorName];
			const isMeaningfulValue =
				currentValue !== undefined &&
				currentValue !== null &&
				currentValue !== '' &&
				!currentValue.startsWith('Error: ');
			if (isMeaningfulValue) {
				newSelectorHistory[selectorName] = [
					{ id: currentValue, name: currentValue }
				];
				selectorToSelectorName.current.set(currentValue, selectorName);
			}
		}
		setSelectorHistory((prevState) => ({
			...prevState,
			...newSelectorHistory
		}));

		return () => {
			newSocket.disconnect();
		};
	}, [getValues]);

	useEffect(() => {
		if (socket === undefined) {
			return;
		}

		socket.removeAllListeners();

		socket.on('connect', () => {
			setWsConnected(true);
			if (conversationId.current) {
				socket.emit('register-socket-for-conversation', {
					conversationUuid: conversationId.current
				});
			}
		});

		socket.on('disconnect', () => {
			setWsConnected(false);
		});

		socket.on('exception', (data: WsExceptionMessage) => {
			setChatMessages((prevItems) => [
				...prevItems,
				{
					t: ChatMessageType.ServerError,
					msg: data.message || ''
				}
			]);
			if (data?.code === 'AUTH_FAILED' || data?.status === 'error') {
				setConversationState(ConversationState.Finished);
			}
		});

		socket.on('server-error', (data: ServerErrorMessage) => {
			setChatMessages((prevItems) => [
				...prevItems,
				{
					t: ChatMessageType.ServerError,
					msg: data.errorMessage
				}
			]);
		});

		socket.on('user-error', (data: ServerErrorMessage) => {
			setChatMessages((prevItems) => [
				...prevItems,
				{
					t: ChatMessageType.UserError,
					msg: data.errorMessage
				}
			]);
		});

		socket.on('state-change', (data: StateChangeCallbackContent) => {
			setConversationState(data.newState);
			if (data.newState === ConversationState.Finished) {
				if (data.conversationResult.t === AIResultType.NoHTML) {
					setChatMessages((prevItems) => [
						...prevItems,
						{
							t: ChatMessageType.ServerError,
							msg: 'HTML konnte nicht gecrawlt werden'
						}
					]);
				} else if (
					data.conversationResult.t === AIResultType.NoProductInHTML
				) {
					setChatMessages((prevItems) => [
						...prevItems,
						{
							t: ChatMessageType.ServerError,
							// @ts-ignore
							msg: `Kein Produkt im HTML gefunden: ${data.conversationResult?.description || ''}`
						}
					]);
				}
			}
		});

		socket.on('ai-message', (data: { message: string }) => {
			setChatMessages((prevItems) => [
				...prevItems,
				{
					t: ChatMessageType.AIMessage,
					msg: data.message
				}
			]);
		});

		socket.on('user-message', (data: { message: string }) => {
			setChatMessages((prevItems) => [
				...prevItems,
				{
					t: ChatMessageType.UserMessage,
					msg: data.message
				}
			]);
		});

		socket.on('new-selector', (data: SelectorWSMessage) => {
			onReceiveSelector(data.key, data.value);
			if (!useAI) {
				setConversationState(undefined);
			}
		});

		socket.on('tool-called', (data: ToolCallWSMessage) => {
			if (
				data.toolName === 'set_selector' &&
				data.output === 'success' &&
				Object.prototype.hasOwnProperty.call(
					data.arguments,
					'selectorName'
				) &&
				Object.prototype.hasOwnProperty.call(
					data.arguments,
					'selectorValue'
				) &&
				// @ts-ignore
				typeof data.arguments['selectorName'] === 'string' &&
				// @ts-ignore
				typeof data.arguments['selectorValue'] === 'string'
			) {
				onReceiveSelector(
					// @ts-ignore
					data.arguments['selectorName'],
					// @ts-ignore
					data.arguments['selectorValue']
				);
			} else {
				setChatMessages((prevItems) => [
					...prevItems,
					{
						t: ChatMessageType.ToolCall,
						...data
					}
				]);
			}
		});

		socket.on('tool-call-success', (data: ToolCallSuccessWSMessage) => {
			if (data.request.toolName === 'use_selector') {
				if (
					data.toolOutput ===
						'Error: tool not available at the moment' ||
					data.toolOutput ===
						'Error: Pseudo-elements are not supported by css-select'
				) {
					return;
				}
				const { selector } = JSON.parse(data.request.toolArguments);
				if (typeof selector === 'string' && selector !== '') {
					const selectorName =
						selectorToSelectorName.current.get(selector);
					if (selectorName) {
						onReceiveActualValue(
							selectorName.replace('selector', 'actual'),
							data.toolOutput
						);
					}
				}
			}
		});
	}, [onReceiveActualValue, onReceiveSelector, socket, useAI]);

	const testAllSelectors = useCallback(async () => {
		if (socketRef.current === undefined) {
			return;
		}

		const values = getValues();
		const promises: Promise<void>[] = [];
		Object.keys(values).forEach((key) => {
			if (key?.startsWith('selector') && values[key]) {
				promises.push(getSelectedValue(key, values[key]));
			}
		});
		await Promise.all(promises);
	}, [getSelectedValue, getValues]);

	const handleSearch = useCallback(() => {
		if (
			socketRef.current === undefined ||
			productUrlRef.current === undefined ||
			productUrlRef.current === ''
		) {
			return;
		}
		setChatMessages([]);
		setConversationState(ConversationState.Other);
		const expectedValues: Record<string, string> = {};
		const values = getValues();
		Object.keys(values).forEach((key) => {
			if (key?.startsWith('expected') && values[key]) {
				expectedValues[key] = values[key];
			}
		});
		const model =
			aiModel === 'other' ? customAIModel || undefined : aiModel;
		const conversationUuid = uuidv4();
		conversationId.current = conversationUuid;
		socketRef.current.emit('find-selectors', {
			conversationUuid,
			productUrl: productUrlRef.current,
			method: crawlingMethod === 'curl' ? null : crawlingMethod,
			model,
			useAI,
			expectedValues
		});
	}, [aiModel, crawlingMethod, customAIModel, getValues, useAI]);

	const handleUserMessage = useCallback((msg: string) => {
		if (socketRef.current === undefined) {
			return false;
		}
		socketRef.current.emit('new-message', {
			conversationUuid: conversationId.current,
			message: msg
		});
		return true;
	}, []);

	const handlePauseConversation = useCallback(() => {
		if (socketRef.current === undefined) {
			return;
		}
		socketRef.current.emit('pause-conversation', {
			conversationUuid: conversationId.current
		});
	}, []);

	const handleDownloadHtml = useCallback(async (): Promise<void> => {
		if (productUrl === undefined) {
			return;
		}
		setIsDownloadingHtml(true);
		try {
			const htmlRes = await axios.post(
				`${process.env.REACT_APP_SELECTOR_TESTER_SERVICE_URL}/shops/get-html`,
				{
					productUrl,
					method: crawlingMethod
				}
			);
			const html = htmlRes.data.html;
			if (typeof html !== 'string' || html.length === 0) {
				return;
			}

			const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
			const objectUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');

			link.href = objectUrl;
			link.download = createHtmlDownloadFilename(
				productUrl,
				crawlingMethod
			);
			link.style.display = 'none';

			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(objectUrl);
		} catch (error: any) {
			console.error(error);
		} finally {
			setIsDownloadingHtml(false);
		}
	}, [productUrl, crawlingMethod]);

	const isSearching = useMemo(() => {
		if (conversationState === undefined) {
			return false;
		}
		if (
			conversationState === ConversationState.Finished ||
			conversationState === ConversationState.WaitingForUserInput
		) {
			return false;
		}
		return true;
	}, [conversationState]);

	const canSearch = useMemo(() => {
		if (productUrl === '') {
			return false;
		}
		return !isSearching;
	}, [isSearching, productUrl]);

	return {
		aiModel,
		canSearch,
		chatMessages,
		conversationState: effectiveConversationState,
		crawlingMethod,
		customAIModel,
		getSelectedValue,
		handlePauseConversation,
		handleSearch,
		handleUserMessage,
		isGettingSomeSelectorValue,
		isSearching,
		productUrl,
		selectorHistory,
		setAIModel,
		setCrawlingMethod,
		setCustomAIModel,
		setProductUrl,
		setSelector,
		setUseAI,
		testAllSelectors,
		handleDownloadHtml,
		useAI,
		isDownloadingHtml,
		availableModels,
	};
}
